<template>
  <uc-renderer
    v-if="display"
    ref="ui-core"
    :data="uicorePlugin.getRootData()"
    :data-schema="uicorePlugin.getRootDataSchema()"
    :ui-lib="uicorePlugin.uiLib"
    :plugins="finalUcPlugins"
    :pagelets="layout"
    :components="components"
    pagelet-id="default"
    :uc-static-renderer="true"
    form-item-order="always-second"
    @uc:report="report"
  />
</template>

<script>
import { $t } from '@/i18n';
import { PageletContext } from '@utils/global/api';
import UICoreXiaoyaoPlugin from '../uicorePlugin/UICoreXiaoyaoPlugin';
import componentWrapByUIcore from '@utils/componentWrapByUIcore';
import XyModal from '@components/modal';
import XyPage from '@components/page';
import XyNav from '@components/builtin/xy-nav';
import XySidebar from '@components/builtin/xy-sidebar';
import XyHeader from '@components/builtin/xy-header';
import XyFooter from '@components/builtin/xy-footer';
import XyPageletContainer from '@components/pagelet';
import XyPageletRenderer from '@components/pagelet-renderer';
import uicoreMixin from '@mixins/uicore-mixin';
import eventBus, { EVENT } from '@utils/event-bus';
import logger from '@utils/logger';
import LessCodePlugin from '@tencent/ui-core/exts/LessCode';
import { generateCodeDeflate, getLessCodeProxyContext } from '@utils/lessCode';

import { message } from '@utils/wuji-message';
import useLayout from '@composables/useLayout';

const layoutTemplate = (name, props, style) => ([{
  id: 'default',
  name: $t('k_5559df05'),
  layout: {
    type: `${name}`,
    style,
    props,
  },
  xyType: 'pagelet',
  // i18n ignore next line
  lessCode: 'export default {\n  data() {\n    return {\n      // param: \'\',      // 自定义变量\n    };\n  },\n  mounted() { },\n  methods: {  },\n};\n',
  ucVersion: 4,
  xySources: [],
  dependencies: [],
  stateSchema: {
    type: 'object',
    fields: [],
  },
}]);

export default {
  name: 'NavCompsUcRender',
  components: {},
  mixins: [uicoreMixin],
  provide() {
    return {
      w: this.w,
      wContext: this.wContext,
      projectId: '',
      pageId: '*',
    };
  },
  props: {
    comp: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'runtime',
    },
  },
  setup() {
    const { project } = useLayout();
    return {
      project,
    };
  },
  data() {
    return {
      w: new PageletContext({ mode: this.mode, pageletContainer: this }),
      lessCodePlugin: null,
      uicorePlugin: new UICoreXiaoyaoPlugin({ parent: this }),
      components: [],
      display: false,
      wContext: {
        childrenData: {}, // 子容器的参数, key 为容器id
        modalList: [], // 页面的弹窗列表
        parentWContext: null, // 父页面的上下文
        isMiniprogram: false, // 是否是小程序
        isNarrowScreen: false, // 是否是窄屏
        // 显示弹窗
        showModal: async (id, data, opt = {}) => {
          logger.info('[modal][pagelet].show.params', id, data, opt);
          const params = opt?.showModal ?? [];
          if (this.wContext.childrenData[id]) {
            this.wContext.childrenData[id].init = true;
            this.wContext.childrenData[id].visible = true;
            this.wContext.childrenData[id].data = data;
            this.wContext.childrenData[id].params = params;

            // [废弃] mergeParams 兼容旧配置
            this.wContext.childrenData[id].mergeParams = opt.mergeParams ?? false;

            this.$nextTick(() => {
              if (this.wContext.lessCode.uc.root.getDtOption()?.open) {
                this.wContext.lessCode.uc.root.getDtBus()
                  .changeUc(this.$refs?.[`wSysModal_${id}`]?.[0]?.wContext?.lessCode?.uc);
              }
            });
            const closeRes = await new Promise((resolve) => {
              // 主页面监听所有弹窗的关闭事件
              this.$once(`dialogClose:${id}`, resolve);
            });

            logger.info('[modal][pagelet].show.res', closeRes);
            return closeRes;
          }
          throw new Error($t('k_cfc697e9', { arg_0: id }));
        },
        // 隐藏弹窗
        hideModal: async (id, ...args) => {
          logger.info('[modal][pagelet].hide', id, ...args);
          if (this.wContext.childrenData[id]) {
            this.wContext.childrenData[id].visible = false;
          } else if (this.parentWContext) {
            this.parentWContext.hideModal(id, ...args);
          }

          await new Promise((rs) => {
            // 延迟 300ms 后触发一次，兼容页面跳转后弹窗组件没有触发动画和 afterClose 事件导致事件流没有执行完毕
            this.$once(`afterDialogClose:${id}`, () => {
              // 向 lessCode 触发弹窗关闭事件
              this.$emit(`dialogClose:${id}`, ...args);
              if (this.wContext.lessCode.uc.root.getDtOption()?.open) {
                this.wContext.lessCode.uc.root.getDtBus()
                  .changeUc(this.wContext.lessCode.uc);
              }
              rs();
            });
          });
        },
      },
      pageId: '*',
    };
  },
  computed: {
    layout() {
      return layoutTemplate(this.comp.name, this.comp.props, this.comp.style);
    },
    useMockContext() {
      return this.mode === 'preview' || this.pageId === '*';
    },
  },
  async created() {
    const projectId = this.project.id;
    this.pageId = this.$route.meta.pageId || this.pageId;
    this.wContext.projectConfig = this.project;
    this.wContext.projectId = projectId;

    if (this.useMockContext) {
      await this.getMockContextForPreview();
    }
    this.lessCodePlugin = new LessCodePlugin({
      deflate: generateCodeDeflate(getLessCodeProxyContext({ w: this.w, $app: this.$app })),
      customCallback: ['componentLoaded'],
    });
    this.initPlugins(projectId, this.pageId);
    /**
     * 通过 eventBus 的方式获取页面的 wContext 和 w
     */
    eventBus.on(EVENT.updatePageContext, this.handlePageContextUpdate);
    eventBus.emit(EVENT.requestPageContext);

    /**
     * 如果是在预览模式 or 没有页面的场景（比如没有设置主页然后点击了访问）
     * 无法通过 eventBus 事件的方式来设置 display：true
     * 这里手动设置一下
     */
    if (this.useMockContext) {
      this.display = true;
    }
  },
  beforeDestroy() {
    eventBus.off(EVENT.updatePageContext, this.handlePageContextUpdate);
  },
  mounted() {
    const compsList = {};
    Object.entries(window.wujiComp.moduleManage.installedModules).forEach(([key, module]) => {
      const { compInfo, compDB } = module;
      if (compInfo && compDB) {
        compsList[key] = { ...compInfo, compDB };
      }
    });

    const finalComponents = Object.keys(compsList)
      .map(key => componentWrapByUIcore(key, compsList[key]))
      .filter(item => !!item);

    const builtin = [XyModal, XyPage, XyNav, XySidebar, XyHeader, XyFooter, XyPageletContainer, XyPageletRenderer];
    finalComponents.push(...builtin);

    this.components = finalComponents;
  },
  methods: {
    async getMockContextForPreview() {
      this.wContext.pageletInstance = this;
      const projectId = this.project.id;
      const env = 'dev';
      const pageList = await this.$store.dispatch('runtime/getBasicPageList', { projectId, env });

      if (!pageList.length) {
        const logger = this.mode === 'preview' ? message : console;
        logger.info($t('k_ead29e14'));
      } else {
        /**
         * 我们使用 pageList中的第一个页面来作为预览模式下的上下文
         */
        const getPreviewPageConfigParams = {
          projectId,
          pageId: pageList[0].pageId,
          env,
          branch: this.$route.query.branch,
        };
        this.wContext.config = await this.$store.dispatch('runtime/getPageDetail', getPreviewPageConfigParams);
      }
    },
    handlePageContextUpdate(context) {
      this.display = false;
      const { wContext, w } = context;
      Object.assign(this.wContext, wContext);
      Object.assign(this.w, w);
      this.display = true;
    },
  },
};
</script>
